import { getUsersOrganizations } from "admin/api/organization";
import {
  getAuthToken,
  getDatasets,
  getReadableAnnotations,
  updateSelectedThemeOfUser,
} from "admin/rest_api";
import type { ItemType } from "antd/es/menu/interface";
import DOMPurify from "dompurify";
import { copyToClipboard } from "libs/clipboard";
import { useWkSelector } from "libs/react_hooks";
import Toast from "libs/toast";
import { getPhraseFromCamelCaseString, isUserAdminOrManager } from "libs/utils";
import capitalize from "lodash-es/capitalize";
import compact from "lodash-es/compact";
import noop from "lodash-es/noop";
import sortBy from "lodash-es/sortBy";
import { getAdministrationSubMenu, getAnalysisSubMenu, switchTo } from "navbar";
import { useCallback, useEffect, useMemo, useState } from "react";
import ReactCommandPalette, { type Command } from "react-command-palette";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { ColorWKBlue, getSystemColorTheme, getThemeFromUser } from "theme";
import { WkDevFlags } from "viewer/api/wk_dev";
import { ViewModeValues } from "viewer/constants";
import { mayEditAnnotation } from "viewer/model/accessors/annotation_accessor";
import { getViewDatasetURL } from "viewer/model/accessors/dataset_accessor";
import { AnnotationTool, Toolkits } from "viewer/model/accessors/tool_accessor";
import { setViewModeAction, updateUserSettingAction } from "viewer/model/actions/settings_actions";
import { setThemeAction, setToolAction } from "viewer/model/actions/ui_actions";
import { setActiveUserAction } from "viewer/model/actions/user_actions";
import type { UserConfiguration } from "viewer/store";
import {
  type TracingViewMenuProps,
  useTracingViewMenuItems,
} from "../action_bar/use_tracing_view_menu_items";
import { viewDatasetMenu } from "../action_bar/view_dataset_actions_view";
import { LayoutEvents, layoutEmitter } from "../layouting/layout_persistence";
import { commandPaletteDarkTheme, commandPaletteLightTheme } from "./command_palette_theme";

// than a theme token.
const commandEntryColor = ColorWKBlue;

type ExtendedCommand = Command & {
  shortcut?: string;
  highlight?: string;
};

type CommandWithoutId = Omit<ExtendedCommand, "id">;

enum DynamicCommands {
  viewDataset = "View Dataset ",
  viewAnnotation = "View Annotation ",
  switchOrganization = "Switch Organization ",
}

const getLabelForAction = (action: NonNullable<ItemType>) => {
  if ("title" in action && action.title != null) {
    return action.title;
  }
  if ("label" in action && action.label != null) {
    return action.label.toString();
  }
  throw new Error("No label found for action");
};

const mapMenuActionsToCommands = (menuActions: Array<ItemType>): CommandWithoutId[] => {
  return compact(
    menuActions.map((action) => {
      if (action == null) {
        return null;
      }
      const onClickAction = "onClick" in action && action.onClick != null ? action.onClick : noop;
      return {
        name: getLabelForAction(action),
        command: onClickAction,
        color: commandEntryColor,
      };
    }),
  );
};

const getLabelForPath = (key: string) =>
  getPhraseFromCamelCaseString(capitalize(key.split("/")[1])) || key;

const cleanStringOfMostHTML = (dirtyString: string | undefined) => {
  if (dirtyString == null) return null;
  return DOMPurify.sanitize(dirtyString, { ALLOWED_TAGS: ["b"] });
};

const shortCutDictForTools: Record<string, string> = {
  [AnnotationTool.MOVE.id]: "Ctrl + K, M",
  [AnnotationTool.SKELETON.id]: "Ctrl + K, S",
  [AnnotationTool.BRUSH.id]: "Ctrl + K, B",
  [AnnotationTool.ERASE_BRUSH.id]: "Ctrl + K, E",
  [AnnotationTool.TRACE.id]: "Ctrl + K, L",
  [AnnotationTool.ERASE_TRACE.id]: "Ctrl + K, R",
  [AnnotationTool.VOXEL_PIPETTE.id]: "Ctrl + K, P",
  [AnnotationTool.QUICK_SELECT.id]: "Ctrl + K, Q",
  [AnnotationTool.BOUNDING_BOX.id]: "Ctrl + K, X",
  [AnnotationTool.PROOFREAD.id]: "Ctrl + K, O",
};

function isEditableElement(target: EventTarget | null): boolean {
  return (
    target instanceof HTMLInputElement ||
    target instanceof HTMLTextAreaElement ||
    target instanceof HTMLSelectElement ||
    (target instanceof HTMLElement && target.isContentEditable)
  );
}

export const CommandPalette = () => {
  const dispatch = useDispatch();

  const userConfig = useWkSelector((state) => state.userConfiguration);
  const isViewMode = useWkSelector((state) => state.temporaryConfiguration.controlMode === "VIEW");
  const isInAnnotationView = useWkSelector((state) => state.uiInformation.isInAnnotationView);

  const restrictions = useWkSelector((state) => state.annotation.restrictions);
  const allowUpdate = useWkSelector(mayEditAnnotation);
  const task = useWkSelector((state) => state.task);
  const annotationType = useWkSelector((state) => state.annotation.annotationType);
  const annotationId = useWkSelector((state) => state.annotation.annotationId);
  const activeUser = useWkSelector((state) => state.activeUser);
  const isAnnotationLockedByUser = useWkSelector((state) => state.annotation.isLockedByOwner);
  const annotationOwner = useWkSelector((state) => state.annotation.owner);

  const navigate = useNavigate();
  const [paletteKey, setPaletteKey] = useState(0);
  const [isOpenRequested, setIsOpenRequested] = useState(false);

  // The palette's own hotkey handling ignores key events from text inputs, so the browser's
  // print dialog would open instead. Handle the hotkey for those elements here.
  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      const isHotkey =
        (event.ctrlKey || event.metaKey) &&
        !event.shiftKey &&
        !event.altKey &&
        // event.code also covers non-Latin keyboard layouts, where event.key isn't "p".
        (event.key.toLowerCase() === "p" || event.code === "KeyP");
      if (isHotkey && isEditableElement(event.target)) {
        event.preventDefault();
        setIsOpenRequested(true);
      }
    };
    window.addEventListener("keydown", onKeyDown, true);
    return () => window.removeEventListener("keydown", onKeyDown, true);
  }, []);

  const props: TracingViewMenuProps = {
    restrictions,
    task,
    annotationType,
    annotationId,
    activeUser,
    isAnnotationLockedByUser,
    annotationOwner,
  };

  const theme = getThemeFromUser(activeUser);

  const getTabsAndSettingsMenuItems = () => {
    if (!isInAnnotationView) return [];
    const commands: CommandWithoutId[] = [];

    (Object.keys(userConfig) as [keyof UserConfiguration]).forEach((key) => {
      if (typeof userConfig[key] === "boolean" && key !== "renderWatermark") {
        // removing the watermark is a paid feature
        commands.push({
          name: `Toggle ${getPhraseFromCamelCaseString(key)}`,
          command: () => dispatch(updateUserSettingAction(key, !userConfig[key])),
          color: commandEntryColor,
        });
      }
    });
    return commands;
  };

  // type annotation due to the library
  const handleSelect = useCallback(async (command: Record<string, unknown>) => {
    if (command.name == null) {
      return;
    }

    if (command.name === DynamicCommands.viewDataset) {
      try {
        const items = await getDatasetItems();
        if (items.length > 0) {
          setCommands(items);
        } else {
          Toast.info("No datasets available.");
        }
      } catch (_e) {
        Toast.error("Failed to load datasets.");
      }
      return;
    }

    if (command.name === DynamicCommands.viewAnnotation) {
      try {
        const items = await getAnnotationItems();
        if (items.length > 0) {
          setCommands(items);
        } else {
          Toast.info("No annotations available.");
        }
      } catch (_e) {
        Toast.error("Failed to load annotations.");
      }
      return;
    }

    if (command.name === DynamicCommands.switchOrganization) {
      try {
        const organizations = await getOrganizationItems();
        if (organizations.length > 0) {
          setCommands(organizations);
        } else {
          Toast.info("No other organizations available.");
        }
      } catch (_e) {
        Toast.error("Failed to load organizations.");
      }
      return;
    }

    closePalette();
  }, []);

  const getDatasetItems = useCallback(async () => {
    const datasets = await getDatasets();
    return datasets.map((dataset) => ({
      name: `View Dataset: ${dataset.name} (id ${dataset.id})`,
      command: () => {
        window.location.href = getViewDatasetURL(dataset);
      },
      color: commandEntryColor,
      id: dataset.id,
    }));
  }, []);

  const viewDatasetsItem = {
    name: DynamicCommands.viewDataset,
    command: () => {},
    shortcut: "Enter to show list",
    color: commandEntryColor,
  };

  const getAnnotationItems = useCallback(async () => {
    const annotations = await getReadableAnnotations(false);
    const sortedAnnotations = sortBy(annotations, (a) => a.modified).reverse();
    return sortedAnnotations.map((annotation) => {
      return {
        name: `View Annotation: ${annotation.name.length > 0 ? `${annotation.name} (id ${annotation.id})` : annotation.id}`,
        command: () => {
          window.location.href = `/annotations/${annotation.id}`;
        },
        color: commandEntryColor,
        id: annotation.id,
      };
    });
  }, []);

  const viewAnnotationsItem = {
    name: DynamicCommands.viewAnnotation,
    shortcut: "Enter to show list",
    command: () => {},
    color: commandEntryColor,
  };

  const getOrganizationItems = useCallback(async () => {
    const organizations = await getUsersOrganizations();
    const otherOrganizations = organizations.filter((orga) => orga.id !== activeUser?.organization);
    return otherOrganizations.map((organization) => {
      return {
        name: `Switch to Organization: ${organization.name}`,
        command: async () => {
          await switchTo(organization);
        },
        color: commandEntryColor,
        id: organization.id,
      };
    });
  }, [activeUser?.organization]);

  const switchOrganizationItem = {
    name: DynamicCommands.switchOrganization,
    shortcut: "Enter to show list",
    command: () => {},
    color: commandEntryColor,
  };

  const getAuthCommands = () => {
    if (activeUser == null) return [];
    return [
      {
        name: "Copy Organization ID",
        command: async () => {
          await copyToClipboard(activeUser.organization, "organization ID");
        },
        color: commandEntryColor,
      },
      {
        name: "Copy Auth Token",
        command: async () => {
          try {
            const token = await getAuthToken();
            await copyToClipboard(token, "auth token");
          } catch (error) {
            Toast.error("Failed to fetch auth token. Please refresh the page to try again.");
            console.error("Failed to fetch auth token:", error);
          }
        },
        color: commandEntryColor,
      },
    ];
  };

  const getSuperUserItems = (): CommandWithoutId[] => {
    if (!activeUser?.isSuperUser) {
      return [];
    }
    return [
      {
        name: "Toggle Action Logging",
        command: () => (WkDevFlags.logActions = !WkDevFlags.logActions),
        color: commandEntryColor,
      },
    ];
  };

  const getNavigationEntries = () => {
    if (activeUser == null) return [];
    const commands: CommandWithoutId[] = [];
    const basicNavigationEntries = [
      { name: "Tasks (Dashboard)", path: "/dashboard/tasks" },
      { name: "Annotations", path: "/dashboard/annotations" },
      { name: "Datasets", path: "/dashboard/datasets" },
      { name: "Time Tracking", path: "/timetracking" },
    ];

    const adminMenu = getAdministrationSubMenu(false, activeUser);
    const adminCommands =
      adminMenu == null
        ? []
        : adminMenu.children.map((entry: { key: string }) => {
            return { name: getLabelForPath(entry.key), path: entry.key };
          });

    const analysisSubMenu = getAnalysisSubMenu(true);
    const analysisCommands =
      analysisSubMenu != null
        ? analysisSubMenu.children.map((entry) => {
            return { name: getLabelForPath(entry.key), path: entry.key };
          })
        : [];

    const statisticsCommands = isUserAdminOrManager(activeUser)
      ? [
          {
            path: "/reports/projectProgress",
            name: "Project Progress",
          },
          {
            path: "/reports/availableTasks",
            name: "Available Tasks",
          },
        ]
      : [];

    const navigationEntries = [
      ...basicNavigationEntries,
      ...adminCommands,
      ...analysisCommands,
      ...statisticsCommands,
    ];

    navigationEntries.forEach((entry) => {
      commands.push({
        name: `Go to ${entry.name}`,
        command: () => {
          navigate(entry.path);
        },
        color: commandEntryColor,
      });
    });

    return commands;
  };

  const getThemeEntries = () => {
    if (activeUser == null) return [];
    const commands: CommandWithoutId[] = [];

    const themesWithNames = [
      ["auto", "System-default"],
      ["light", "Light"],
      ["dark", "Dark"],
    ] as const;

    for (let [theme, name] of themesWithNames) {
      commands.push({
        name: `Switch to “${name}” color theme`,
        command: async () => {
          if (theme === "auto") theme = getSystemColorTheme();

          const newUser = await updateSelectedThemeOfUser(activeUser.id, theme);
          dispatch(setThemeAction(theme));
          dispatch(setActiveUserAction(newUser));
        },
        color: commandEntryColor,
      });
    }

    return commands;
  };

  const getViewModeEntries = () => {
    if (!isInAnnotationView) return [];
    const commands = ViewModeValues.map((mode) => ({
      name: `Switch to ${mode} mode`,
      command: () => {
        dispatch(setViewModeAction(mode));
      },
      color: commandEntryColor,
    }));
    commands.push({
      name: "Reset layout",
      command: () => layoutEmitter.emit(LayoutEvents.resetLayout),
      color: commandEntryColor,
    });
    return commands;
  };

  const getToolEntries = useCallback(() => {
    if (!isInAnnotationView) return [];
    const commands: CommandWithoutId[] = [];
    let availableTools = Object.values(AnnotationTool);
    if (isViewMode || !allowUpdate) {
      availableTools = Toolkits.READ_ONLY_TOOLS;
    }
    availableTools.forEach((tool) => {
      commands.push({
        name: `Switch to ${tool.readableName}`,
        command: () => dispatch(setToolAction(tool)),
        shortcut: shortCutDictForTools[tool.id] || "",
        color: commandEntryColor,
      });
    });
    return commands;
  }, [isInAnnotationView, isViewMode, allowUpdate, dispatch]);

  const tracingMenuItems = useTracingViewMenuItems(props, null);

  const menuActions = useMemo(() => {
    if (!isInAnnotationView) return [];
    if (isViewMode) {
      return viewDatasetMenu;
    }
    return tracingMenuItems;
  }, [isInAnnotationView, isViewMode, tracingMenuItems]);

  const allStaticCommands = [
    viewDatasetsItem,
    viewAnnotationsItem,
    switchOrganizationItem,
    ...getNavigationEntries(),
    ...getThemeEntries(),
    ...getToolEntries(),
    ...getViewModeEntries(),
    ...mapMenuActionsToCommands(menuActions),
    ...getTabsAndSettingsMenuItems(),
    ...getSuperUserItems(),
    ...getAuthCommands(),
  ];

  const [commands, setCommands] = useState<CommandWithoutId[]>(allStaticCommands);

  // Rerun when inputs that the static commands close over change. userConfig is included so
  // the "Toggle …" commands capture the current boolean value and actually flip it instead of
  // repeatedly applying the value that was current when the palette mounted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: see comment above
  useEffect(() => {
    setCommands(allStaticCommands);
  }, [allowUpdate, userConfig]);

  const closePalette = () => {
    setIsOpenRequested(false);
    setPaletteKey((prevKey) => prevKey + 1);
  };

  const commandsWithIds = useMemo(() => {
    return commands.map((command, index) => {
      return { id: index, ...command };
    });
  }, [commands]);

  return (
    <ReactCommandPalette
      commands={commandsWithIds}
      key={paletteKey}
      hotKeys={["ctrl+p", "command+p"]}
      open={isOpenRequested}
      trigger={null}
      maxDisplayed={100}
      theme={theme === "light" ? commandPaletteLightTheme : commandPaletteDarkTheme}
      onSelect={handleSelect}
      showSpinnerOnSelect={false}
      resetInputOnOpen
      onRequestClose={() => {
        setIsOpenRequested(false);
        setCommands(allStaticCommands);
      }}
      closeOnSelect={false}
      renderCommand={(command) => {
        const { shortcut, highlight: maybeDirtyString, name } = command as ExtendedCommand;
        const cleanString = cleanStringOfMostHTML(maybeDirtyString);
        return (
          <div
            className="item"
            style={{ display: "flex", justifyContent: "space-between", width: "100%" }}
          >
            {cleanString ? (
              // biome-ignore lint/security/noDangerouslySetInnerHtml: modified from https://github.com/asabaylus/react-command-palette/blob/main/src/default-command.js
              <span dangerouslySetInnerHTML={{ __html: cleanString }} />
            ) : (
              <span>{name}</span>
            )}
            <span>{shortcut}</span>
          </div>
        );
      }}
    />
  );
};
